<template>
  <gamanza-tournament-notification :skin="skin" :tournament="activeTournament" :debug="isTournamentDebug"
    :tournament-count="activeTournamentCount" :language="language" @dismiss="dismissActiveTournament"
    @open="openTournaments" />
</template>

<script>
import gamanzaWidgetService from '@/services/gamanzaWidgetService';
import GamanzaTournamentNotification from '@/components/gamanza/GamanzaTournamentNotification.vue';

export default {
  name: 'GamanzaInit',
  props: ['skin'],
  components: {
    GamanzaTournamentNotification
  },
  data() {
    return {
      sdkReady: false,
      initTimeout: null,
      activeTournament: null,
      activeTournamentCount: 0,
      notifiedTournamentKeys: []
    }
  },
  computed: {
    isTournamentDebug() {
      return this.$route?.query?.gamanzaDebugTournament === 'true';
    },
    user() {
      return this.$store.getters['getUserData'];
    },
    identityToken() {
      if (!this.user?.token) return '';
      const raw = this.user.token;
      const siteId = this.user.site_id;
      const token = raw.endsWith('-jugar') ? raw : `${raw}${siteId}-jugar`;
      return token;
    },
    language() {
      return this.$store.getters['getLanguage'] || this.skin?.gamanza?.playerLocale || 'es';
    },
    canInit() {
      const gamanza = this.skin?.gamanza;
      const userId = this.user?.user_data?.data?.user_id;
      const allowedUserIds = gamanza?.allowedUserIds;
      const hasAccess = !Array.isArray(allowedUserIds) ||
        allowedUserIds.length === 0 ||
        allowedUserIds.map(String).includes(String(userId));

      return Boolean(
        this.user?.token &&
        gamanza?.enabled &&
        hasAccess &&
        this.identityToken
      );
    }
  },
  mounted() {
    if (this.canInit && !this.sdkReady) {
      this.scheduleInit();
    }
  },
  methods: {
    scheduleInit() {
      if (this.initTimeout) {
        clearTimeout(this.initTimeout);
      }

      this.initTimeout = setTimeout(() => {
        this.initTimeout = null;
        this.initSDK();
      }, 2000);
    },
    async initSDK() {
      this.$store.commit('setGamanzaReady', false);

      if (!this.canInit) return;

      this.sdkReady = true;

      try {
        await gamanzaWidgetService.init({
          gamanzaConfig: this.skin.gamanza,
          identityToken: this.identityToken
        });
        this.$store.commit('setGamanzaReady', true);
        await this.loadActiveTournaments();
      } catch (error) {
        this.sdkReady = false;
        this.$store.commit('setGamanzaReady', false);
        console.error('Error initializing Gamanza SDK:', error);
      }
    },
    async loadActiveTournaments() {
      if (!this.skin?.gamanza?.mainFrame?.tournaments) return;

      try {
        const tournaments = await gamanzaWidgetService.getPlayerActiveTournaments();
        const activeTournaments = (tournaments || []).filter((tournament) => tournament.status === 'active');
        const unseenTournaments = activeTournaments.filter((tournament) => !this.wasTournamentNotified(tournament));

        if (!unseenTournaments.length) {
          if (!this.isTournamentDebug) return;

          this.activeTournament = {
            externalDetails: [{
              language: this.language,
              name: 'No hay torneos activos'
            }]
          };
          this.activeTournamentCount = 0;
          return;
        }

        unseenTournaments.forEach((tournament) => this.markTournamentNotified(tournament));
        this.activeTournament = unseenTournaments[0];
        this.activeTournamentCount = activeTournaments.length;
      } catch (error) {
        console.error('Error loading active Gamanza tournaments:', error);
      }
    },
    tournamentNotificationKey(tournament) {
      const userId = this.user?.user_data?.data?.user_id || '';
      const tournamentId = tournament._id || tournament.timePeriod?.startDate || '';
      return `gamanza-active-tournament:${userId}:${tournamentId}`;
    },
    wasTournamentNotified(tournament) {
      return this.notifiedTournamentKeys.includes(this.tournamentNotificationKey(tournament));
    },
    markTournamentNotified(tournament) {
      const notificationKey = this.tournamentNotificationKey(tournament);
      if (!this.notifiedTournamentKeys.includes(notificationKey)) {
        this.notifiedTournamentKeys.push(notificationKey);
      }
    },
    clearTournamentNotifications() {
      this.notifiedTournamentKeys = [];
    },
    dismissActiveTournament() {
      this.activeTournament = null;
      this.activeTournamentCount = 0;
    },
    openTournaments() {
      this.dismissActiveTournament();
      this.$store.dispatch('changeView', 'gamanza_tournaments');
    },
    destroyGamanza() {
      this.dismissActiveTournament();
      gamanzaWidgetService.destroy();
    }
  },
  watch: {
    'user.token'(newVal, oldVal) {
      if (this.initTimeout) {
        clearTimeout(this.initTimeout);
        this.initTimeout = null;
      }

      if (!newVal) {
        this.clearTournamentNotifications();
        this.destroyGamanza();
        this.sdkReady = false;
        this.$store.commit('setGamanzaReady', false);
        return;
      }

      if (oldVal && oldVal !== newVal) {
        this.clearTournamentNotifications();
        this.destroyGamanza();
        this.sdkReady = false;
        this.$store.commit('setGamanzaReady', false);
      }

      if (this.skin?.gamanza?.enabled) {
        this.$nextTick(() => {
          this.scheduleInit();
        });
      }
    }
  },
  beforeDestroy() {
    if (this.initTimeout) {
      clearTimeout(this.initTimeout);
    }

    this.destroyGamanza();
    this.sdkReady = false;
    this.$store.commit('setGamanzaReady', false);
  }
}
</script>
